// « À propos » (M05) et résultats de la recherche globale (M04), charte D-038.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAPropos } from '../../api/requetes';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, libelleCategorie, libelleConfidentialite, libelleProvince, libelleTypeOrganisation } from '../../composants/format';

export function PageAPropos() {
  const r = useAPropos();
  if (r.isLoading) return <Squelette lignes={6} />;
  if (r.error) return <MessageErreur erreur={r.error} />;
  const a = r.data!;
  // L'intitulé porte son statut entre parenthèses : il est affiché à part, plus discret.
  const [intitule, statut] = a.intitule.split(' (');
  return (
    <div className="page-a-propos">
      <section className="a-propos-tete" aria-labelledby="titre-a-propos">
        <div className="a-propos-logos">
          <img src="/marque/ministere.png" alt="Ministère du Pétrole et du Gaz, République gabonaise" className="logo-ministere seulement-jour" />
          <img src="/marque/ministere-clair.png" alt="Ministère du Pétrole et du Gaz, République gabonaise" className="logo-ministere seulement-nuit" />
          <span className="separateur" aria-hidden="true" />
          <img src="/marque/pictogramme.png" alt="CarburantTrack" className="logo-produit seulement-jour" />
          <img src="/marque/pictogramme-clair.png" alt="CarburantTrack" className="logo-produit seulement-nuit" />
        </div>
        <div className="a-propos-texte">
          <h1 id="titre-a-propos">À propos de {a.nom}</h1>
          <p className="intitule">{intitule}.</p>
          {statut && <p className="attenue">{statut.replace(/\)$/, '').replace(/^./, (c) => c.toUpperCase())}.</p>}
        </div>
      </section>

      {a.environnement.mention && (
        <section className="bandeau-environnement" aria-labelledby="titre-environnement">
          <span className="etiquette-environnement">{a.environnement.nom ?? a.environnement.type}</span>
          <h2 id="titre-environnement">{a.environnement.mention}</h2>
          <span className="attenue">
            {a.environnement.donneesFictives ? `Jeu chargé le ${dateHeure(a.environnement.dateChargementJeu)} (graine ${a.environnement.graine})` : 'Aucun jeu fictif chargé'}
          </span>
        </section>
      )}

      <div className="grille-2">
        <section className="carte-bloc" aria-labelledby="titre-fiche">
          <h2 id="titre-fiche">Fiche</h2>
          <dl className="paires">
            <div>
              <dt>Version</dt>
              <dd>{a.version}</dd>
            </div>
            <div>
              <dt>Modules construits</dt>
              <dd>jusqu’au lot {a.lotConstruit}</dd>
            </div>
            <div>
              <dt>Maître d’ouvrage</dt>
              <dd>{a.maitreOuvrage}</dd>
            </div>
            <div>
              <dt>Administration fonctionnelle</dt>
              <dd>{a.administrateurFonctionnel}</dd>
            </div>
            <div>
              <dt>Exploitation technique</dt>
              <dd>{a.exploitationTechnique}</dd>
            </div>
          </dl>
        </section>
        <section className="carte-bloc" aria-labelledby="titre-attributions">
          <h2 id="titre-attributions">Attributions</h2>
          <ul className="liste-attributions">
            {a.attributions.map((x) => (
              <li key={x.objet}>
                <b>{x.objet}</b>
                <span>{x.texte}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <p className="attenue">{a.reserves}</p>
    </div>
  );
}

interface Resultat {
  type: string;
  id: string;
  libelle: string;
  detail: string | null;
  chemin: string;
}

const GROUPES: { type: string; titre: string }[] = [
  { type: 'installation', titre: 'Installations' },
  { type: 'organisation', titre: 'Organisations' },
  { type: 'localite', titre: 'Localités' },
  { type: 'document', titre: 'Documents' },
];

const ABREVIATIONS_CATEGORIES: Record<string, string> = {
  station: 'ST',
  depot: 'DÉP',
  raffinerie: 'RAF',
  centre_emplissage: 'GPL',
  centre_chargement: 'CHG',
  client_industriel: 'IND',
  site_strategique: 'SIT',
};

/** Pictogramme abrégé et détail lisible, selon le type du résultat (le serveur renvoie des codes). */
function presenter(r: Resultat): { abreviation: string; detail: string | null } {
  if (r.type === 'installation') {
    const [categorie, province] = (r.detail ?? '').split(', ');
    return { abreviation: ABREVIATIONS_CATEGORIES[categorie ?? ''] ?? 'INS', detail: [categorie ? libelleCategorie(categorie) : null, province?.startsWith('GA-') ? libelleProvince(province) : province].filter(Boolean).join(', ') };
  }
  if (r.type === 'organisation') return { abreviation: 'ORG', detail: r.detail ? libelleTypeOrganisation(r.detail) : null };
  if (r.type === 'localite') return { abreviation: 'LOC', detail: r.detail ? `${libelleProvince(r.detail)}, installations proches` : null };
  if (r.type === 'document') return { abreviation: 'DOC', detail: r.detail ? libelleConfidentialite(r.detail) : null };
  return { abreviation: '', detail: r.detail };
}

/** Grand champ de recherche ; réinitialisé à chaque nouvelle requête (clé de rendu). */
function ChampRecherche({ initiale }: { initiale: string }) {
  const naviguer = useNavigate();
  const [saisie, setSaisie] = useState(initiale);
  const rechercher = (e: FormEvent) => {
    e.preventDefault();
    if (saisie.trim().length >= 2) naviguer(`/recherche?q=${encodeURIComponent(saisie.trim())}`);
  };
  return (
    <form className="champ-recherche" role="search" onSubmit={rechercher}>
      <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <label htmlFor="recherche-page" className="visuellement-cache">
        Rechercher dans CarburantTrack
      </label>
      <input id="recherche-page" type="search" value={saisie} placeholder="Installation, organisation, localité, document" onChange={(e) => setSaisie(e.target.value)} />
      <button type="submit" className="bouton primaire" disabled={saisie.trim().length < 2}>
        Rechercher
      </button>
    </form>
  );
}

export function PageRecherche() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  // Le type choisi ne vaut que pour la requête où il a été choisi.
  const [choix, setChoix] = useState({ q, groupe: 'tous' });
  const groupe = choix.q === q ? choix.groupe : 'tous';
  const setGroupe = (g: string) => setChoix({ q, groupe: g });
  const r = useQuery({
    queryKey: ['recherche', q],
    queryFn: async () => (await api<Resultat[]>(`/api/v1/recherche?q=${encodeURIComponent(q)}`)).donnees,
    enabled: q.length >= 2,
  });
  const resultats = r.data ?? [];
  const groupes = GROUPES.map((g) => ({ ...g, elements: resultats.filter((x) => x.type === g.type) })).filter((g) => g.elements.length > 0);
  const affiches = groupes.filter((g) => groupe === 'tous' || g.type === groupe);

  return (
    <div className="page-recherche">
      <ChampRecherche key={q} initiale={q} />

      {q.length < 2 && <p className="attenue">Saisissez au moins deux caractères.</p>}
      {r.isLoading && <Squelette lignes={4} />}
      {r.error && <MessageErreur erreur={r.error} />}
      {r.data && (
        <div className="page-tete">
          <div>
            <h1>
              {resultats.length === 0 ? 'Aucun résultat' : `${resultats.length} résultat${resultats.length > 1 ? 's' : ''}`} pour « {q} »
            </h1>
            <p>Recherche limitée aux objets que vos habilitations permettent de lire.</p>
          </div>
          {groupes.length > 1 && (
            <div className="segments" role="tablist" aria-label="Type de résultat">
              <button type="button" role="tab" aria-selected={groupe === 'tous'} onClick={() => setGroupe('tous')}>
                Tout<span className="compte">{resultats.length}</span>
              </button>
              {groupes.map((g) => (
                <button key={g.type} type="button" role="tab" aria-selected={groupe === g.type} onClick={() => setGroupe(g.type)}>
                  {g.titre}
                  <span className="compte">{g.elements.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {affiches.map((g) => (
        <section key={g.type} className="groupe-resultats" aria-labelledby={`groupe-${g.type}`}>
          <h2 id={`groupe-${g.type}`} className="sur-titre">
            {g.titre}
          </h2>
          <ul className="liste-lignes">
            {g.elements.map((x) => {
              const p = presenter(x);
              return (
                <li key={`${x.type}-${x.id}`}>
                  <Link to={x.chemin}>
                    <span className="pictogramme" aria-hidden="true">
                      {p.abreviation}
                    </span>
                    <span className="texte">
                      <b>{x.libelle}</b>
                      {p.detail && <small>{p.detail}</small>}
                    </span>
                    <Icone nom="chevron" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
